import { useEffect, useState } from "react";
import { useRecordContext } from "ra-core";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { Star } from "lucide-react";

import type { Contact, PointsLedgerEntry } from "../types";
import { VipBadge, VIP_THRESHOLDS } from "./VipBadge";

export const PointsLedger = () => {
  const record = useRecordContext<Contact>();

  const [totalPoints, setTotalPoints] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!record) return;
    setLoading(true);
    getSupabaseClient()
      .from("points_ledger")
      .select("delta")
      .eq("contact_id", record.id)
      .then(({ data }) => {
        if (data) {
          setTotalPoints(
            (data as Pick<PointsLedgerEntry, "delta">[]).reduce(
              (sum, e) => sum + e.delta,
              0,
            ),
          );
        }
        setLoading(false);
      });
  }, [record?.id]);

  if (!record) return null;

  // Next VIP level progress
  const nextTier = VIP_THRESHOLDS.find(
    (t) => t.min > (record.total_spent ?? 0),
  );
  const currentTier = [...VIP_THRESHOLDS]
    .reverse()
    .find((t) => t.min <= (record.total_spent ?? 0));
  const progressPct =
    nextTier && currentTier
      ? Math.min(
          100,
          (((record.total_spent ?? 0) - currentTier.min) /
            (nextTier.min - currentTier.min)) *
            100,
        )
      : 100;

  return (
    <div className="space-y-3">
      {/* VIP + total spent summary */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <VipBadge level={record.vip_level ?? "standard"} />
          {record.vip_level === "standard" && (
            <span className="text-xs text-muted-foreground">Standard</span>
          )}
        </div>
      </div>

      {/* Points balance */}
      <div className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2">
        <span className="text-xs text-muted-foreground">积分余额</span>
        <div className="flex items-center gap-1 text-sm font-semibold">
          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
          {loading ? "—" : `${totalPoints.toLocaleString()} pts`}
        </div>
      </div>

      {/* Spend progress bar */}
      {nextTier && (
        <div>
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>${(record.total_spent ?? 0).toLocaleString()} spent</span>
            <span>
              ${nextTier.min.toLocaleString()} for{" "}
              {nextTier.label.split(" ")[0]}
            </span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
